import type { CartItem, Enquiry, EnquiryInput, Order, OrderInput, PaymentStatus } from '../types';
import { authState } from './auth';
import { supabase, isSupabaseConfigured } from './supabase';

/**
 * Buyer/seller desk local data — scoped by auth user id when signed in,
 * otherwise `:guest`. Theme/currency stay global elsewhere.
 *
 * Legacy unscoped keys (forgelink_cart, etc.) are abandoned on scope switch
 * so accounts never share carts/orders/enquiries via localStorage.
 */
const BASE_KEYS = {
  cart: 'forgelink_cart',
  enquiries: 'forgelink_enquiries',
  orders: 'forgelink_orders',
} as const;

const GUEST_SCOPE = 'guest';

let currentScope = GUEST_SCOPE;

function scopedKey(base: string): string {
  return `${base}:${currentScope}`;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Drop legacy unscoped keys so they cannot leak across accounts. */
export function abandonLegacyUnscopedKeys(): void {
  for (const base of Object.values(BASE_KEYS)) {
    try {
      localStorage.removeItem(base);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Switch localStorage namespace for cart / orders / enquiries.
 * Does NOT merge data between scopes (isolate over merge).
 */
export function switchStorageScope(userId: string | null | undefined): {
  changed: boolean;
  scope: string;
} {
  const next = userId ? String(userId) : GUEST_SCOPE;
  const changed = next !== currentScope;
  currentScope = next;
  // Always remove legacy unscoped keys once scoped storage is in use.
  abandonLegacyUnscopedKeys();
  return { changed, scope: currentScope };
}

/** Align storage scope with current authState.user. */
export function syncStorageScopeFromAuth(): { changed: boolean; scope: string } {
  return switchStorageScope(authState.user?.id ?? null);
}

export function getStorageScope(): string {
  return currentScope;
}

export function getCart(): CartItem[] {
  return read<CartItem[]>(scopedKey(BASE_KEYS.cart), []);
}

export function setCart(items: CartItem[]): void {
  write(scopedKey(BASE_KEYS.cart), items);
}

export function getLocalEnquiries(): Enquiry[] {
  return read<Enquiry[]>(scopedKey(BASE_KEYS.enquiries), []);
}

function normalizeOrder(raw: Order): Order {
  return {
    ...raw,
    status: raw.status || 'placed',
    paymentStatus: raw.paymentStatus || (raw.status === 'confirmed' ? 'paid' : 'pending'),
    currency: raw.currency || 'NGN',
    items: raw.items || [],
  };
}

export function getLocalOrders(): Order[] {
  return read<Order[]>(scopedKey(BASE_KEYS.orders), []).map(normalizeOrder);
}

function writeEnquiries(list: Enquiry[]): void {
  write(scopedKey(BASE_KEYS.enquiries), list);
}

function writeOrders(list: Order[]): void {
  write(scopedKey(BASE_KEYS.orders), list);
}

export async function saveEnquiry(enquiry: EnquiryInput): Promise<Enquiry> {
  const withMeta: Enquiry = {
    ...enquiry,
    id: enquiry.id || crypto.randomUUID(),
    status: 'new',
    createdAt: enquiry.createdAt || new Date().toISOString(),
  };

  const list = getLocalEnquiries();
  list.unshift(withMeta);
  writeEnquiries(list);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from('enquiries').insert({
        id: withMeta.id,
        product_id: withMeta.productId,
        product_name: withMeta.productName,
        factory_name: withMeta.factoryName,
        company: withMeta.company,
        contact_name: withMeta.contact,
        email: withMeta.email,
        phone: withMeta.phone || null,
        quantity: withMeta.quantity,
        message: withMeta.message,
        status: withMeta.status,
        user_id: authState.user?.id || null,
      });
    } catch (err) {
      console.warn('Supabase enquiry sync skipped:', err);
    }
  }

  return withMeta;
}

export async function saveOrder(order: OrderInput): Promise<Order> {
  const withMeta: Order = {
    ...order,
    id: order.id || `FL-${Date.now().toString(36).toUpperCase()}`,
    status: order.status || 'placed',
    paymentStatus: order.paymentStatus || 'pending',
    paymentMode: order.paymentMode || 'none',
    currency: order.currency || 'NGN',
    createdAt: order.createdAt || new Date().toISOString(),
  };

  const list = getLocalOrders();
  list.unshift(withMeta);
  writeOrders(list);
  setCart([]);

  if (isSupabaseConfigured && supabase) {
    try {
      const row: Record<string, unknown> = {
        id: withMeta.id,
        company: withMeta.company,
        contact_name: withMeta.contact,
        email: withMeta.email,
        phone: withMeta.phone || null,
        ship_address: withMeta.address,
        delivery_notes: withMeta.notes || null,
        merchandise_total: withMeta.merchandise,
        freight_total: withMeta.freight,
        grand_total: withMeta.total,
        status: withMeta.status,
        user_id: authState.user?.id || null,
        payment_status: withMeta.paymentStatus,
        payment_mode: withMeta.paymentMode || null,
        payment_ref: withMeta.paymentRef || null,
        paid_at: withMeta.paidAt || null,
        currency: withMeta.currency || 'NGN',
      };
      const { error } = await supabase.from('orders').insert(row);
      if (error) {
        // Migration 006 may not be applied yet — retry without payment columns.
        console.warn('Order insert with payment columns failed, retrying base columns:', error.message);
        await supabase.from('orders').insert({
          id: withMeta.id,
          company: withMeta.company,
          contact_name: withMeta.contact,
          email: withMeta.email,
          phone: withMeta.phone || null,
          ship_address: withMeta.address,
          delivery_notes: withMeta.notes || null,
          merchandise_total: withMeta.merchandise,
          freight_total: withMeta.freight,
          grand_total: withMeta.total,
          status: withMeta.status,
          user_id: authState.user?.id || null,
        });
      }
      if (withMeta.items?.length) {
        await supabase.from('order_items').insert(
          withMeta.items.map((it) => ({
            order_id: withMeta.id,
            product_id: it.productId,
            product_name: it.name,
            factory_name: it.factory,
            sku: it.sku,
            unit_price: it.unitPrice,
            quantity: it.qty,
            line_total: it.lineTotal,
          })),
        );
      }
    } catch (err) {
      console.warn('Supabase order sync skipped:', err);
    }
  }

  return withMeta;
}

export function getLocalOrderById(id: string): Order | undefined {
  return getLocalOrders().find((o) => o.id === id);
}

export async function updateOrderPayment(
  orderId: string,
  patch: {
    paymentStatus: PaymentStatus;
    paymentMode?: Order['paymentMode'];
    paymentRef?: string;
    paidAt?: string;
    status?: string;
  },
): Promise<Order | null> {
  const list = getLocalOrders();
  const idx = list.findIndex((o) => o.id === orderId);
  if (idx < 0) return null;
  const next: Order = {
    ...list[idx],
    paymentStatus: patch.paymentStatus,
    paymentMode: patch.paymentMode ?? list[idx].paymentMode,
    paymentRef: patch.paymentRef ?? list[idx].paymentRef,
    paidAt: patch.paidAt ?? list[idx].paidAt,
    status: patch.status ?? list[idx].status,
  };
  list[idx] = next;
  writeOrders(list);

  if (isSupabaseConfigured && supabase) {
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          payment_status: next.paymentStatus,
          payment_mode: next.paymentMode || null,
          payment_ref: next.paymentRef || null,
          paid_at: next.paidAt || null,
          status: next.status,
        })
        .eq('id', orderId);
      if (error) {
        console.warn('Supabase payment update skipped:', error.message);
      }
    } catch (err) {
      console.warn('Supabase payment update skipped:', err);
    }
  }

  return next;
}

export async function fetchRemoteEnquiries(): Promise<Enquiry[]> {
  if (!isSupabaseConfigured || !supabase) return getLocalEnquiries();
  try {
    const { data, error } = await supabase
      .from('enquiries')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((row: Record<string, unknown>) => ({
      id: String(row.id),
      productId: String(row.product_id),
      productName: String(row.product_name),
      factoryName: String(row.factory_name ?? ''),
      company: String(row.company),
      contact: String(row.contact_name),
      email: String(row.email),
      phone: row.phone != null ? String(row.phone) : undefined,
      quantity: Number(row.quantity),
      message: String(row.message),
      status: String(row.status ?? 'new'),
      createdAt: String(row.created_at),
    }));
  } catch (err) {
    console.warn('Falling back to local enquiries:', err);
    return getLocalEnquiries();
  }
}

export async function fetchRemoteOrders(): Promise<Order[]> {
  if (!isSupabaseConfigured || !supabase) return getLocalOrders();
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((row: Record<string, unknown>) => {
      const itemsRaw = (row.order_items as Record<string, unknown>[] | null) || [];
      return {
        id: String(row.id),
        company: String(row.company),
        contact: String(row.contact_name),
        email: String(row.email),
        phone: row.phone != null ? String(row.phone) : undefined,
        address: String(row.ship_address ?? ''),
        notes: row.delivery_notes != null ? String(row.delivery_notes) : undefined,
        merchandise: Number(row.merchandise_total),
        freight: Number(row.freight_total),
        total: Number(row.grand_total),
        status: String(row.status ?? 'placed'),
        paymentStatus: (String(row.payment_status || 'pending') as PaymentStatus),
        paymentMode: (row.payment_mode != null ? String(row.payment_mode) : undefined) as Order['paymentMode'],
        paymentRef: row.payment_ref != null ? String(row.payment_ref) : undefined,
        paidAt: row.paid_at != null ? String(row.paid_at) : undefined,
        currency: (row.currency != null ? String(row.currency) : 'NGN') as Order['currency'],
        createdAt: String(row.created_at),
        items: itemsRaw.map((it) => ({
          productId: String(it.product_id),
          name: String(it.product_name),
          factory: String(it.factory_name),
          sku: String(it.sku),
          unitPrice: Number(it.unit_price),
          qty: Number(it.quantity),
          lineTotal: Number(it.line_total),
        })),
      };
    });
  } catch (err) {
    console.warn('Falling back to local orders:', err);
    return getLocalOrders();
  }
}
